import type {
  GroupField,
  FormField,
  FormRowField,
} from '@components/pages/FormBuilder/types'

const createId = (prefix: string): string =>
  `${prefix}-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`}`

/**
 * Default form content for the "New Patient Form" template (pending).
 * - Personal Information: First/Middle/Last (row); DOB, Gender (standalone)
 * - Contact Information: Mobile/Telephone, Email (standalone)
 * - Residential Address: Address input inside form row
 */
export function getNewPatientFormDefaultGroups(): GroupField[] {
  const personalInfo: GroupField = {
    id: createId('group'),
    name: 'Personal Information',
    required: false,
    autoFill: false,
    fields: [
      {
        id: createId('form-row'),
        type: 'Form Row',
        fields: [
          { id: createId('field'), type: 'Input Field', label: 'First name' },
          { id: createId('field'), type: 'Input Field', label: 'Middle name' },
          { id: createId('field'), type: 'Input Field', label: 'Last name' },
        ] as FormField[],
      } as FormRowField,
      { id: createId('field'), type: 'Date Field', label: 'Date of Birth' },
      {
        id: createId('field'),
        type: 'Radio Group',
        label: 'Gender',
        options: ['Male', 'Female', 'Other'],
      },
    ],
  }

  const contactInfo: GroupField = {
    id: createId('group'),
    name: 'Contact Information',
    required: false,
    autoFill: false,
    fields: [
      { id: createId('field'), type: 'Input Field', label: 'Mobile/Telephone' },
      { id: createId('field'), type: 'Input Field', label: 'Email' },
    ],
  }

  const residentialAddress: GroupField = {
    id: createId('group'),
    name: 'Residential Address',
    required: false,
    autoFill: false,
    fields: [
      {
        id: createId('form-row'),
        type: 'Form Row',
        fields: [
          { id: createId('field'), type: 'Input Field', label: 'Address' },
        ] as FormField[],
      } as FormRowField,
    ],
  }

  return [personalInfo, contactInfo, residentialAddress]
}

/**
 * Default form content for "Returning Patient Form".
 * - Personal Information: Name row, DOB, Gender
 * - Contact Information: Phone, Email
 * - Allergies: Checkboxes (Penicillin, Local Anesthesia, Latex)
 * - Patient's Signature
 */
export function getReturningPatientFormDefaultGroups(): GroupField[] {
  const personalInfo: GroupField = {
    id: createId('group'),
    name: 'Personal Information',
    required: false,
    autoFill: false,
    fields: [
      {
        id: createId('form-row'),
        type: 'Form Row',
        fields: [
          { id: createId('field'), type: 'Input Field', label: 'First name' },
          { id: createId('field'), type: 'Input Field', label: 'Middle name' },
          { id: createId('field'), type: 'Input Field', label: 'Last name' },
        ] as FormField[],
      } as FormRowField,
      { id: createId('field'), type: 'Date Field', label: 'Date of Birth' },
      {
        id: createId('field'),
        type: 'Radio Group',
        label: 'Gender',
        options: ['Male', 'Female', 'Other'],
      },
    ],
  }

  const contactInfo: GroupField = {
    id: createId('group'),
    name: 'Contact Information',
    required: false,
    autoFill: false,
    fields: [
      { id: createId('field'), type: 'Input Field', label: 'Mobile/Telephone' },
      { id: createId('field'), type: 'Input Field', label: 'Email' },
    ],
  }

  const residentialAddress: GroupField = {
    id: createId('group'),
    name: 'Residential Address',
    required: false,
    autoFill: false,
    fields: [
      {
        id: createId('form-row'),
        type: 'Form Row',
        fields: [
          { id: createId('field'), type: 'Input Field', label: 'Address' },
        ] as FormField[],
      } as FormRowField,
    ],
  }

  const allergies: GroupField = {
    id: createId('group'),
    name: 'Allergies',
    required: false,
    autoFill: false,
    fields: [
      { id: createId('field'), type: 'Checkbox', label: 'Penicillin' },
      { id: createId('field'), type: 'Checkbox', label: 'Local Anesthesia' },
      { id: createId('field'), type: 'Checkbox', label: 'Latex' },
    ],
  }

  const signature: GroupField = {
    id: createId('group'),
    name: "Patient's Signature",
    required: false,
    autoFill: false,
    fields: [
      {
        id: createId('field'),
        type: 'Signature Field',
        label: "Patient's Signature",
      },
    ],
  }

  return [personalInfo, contactInfo, residentialAddress, allergies, signature]
}

/**
 * Default form content for "Insurance Information Form".
 * - Insurance Details: Carrier, Policy number, Group number, Subscriber
 * - Patient Relationship to Subscriber
 * - Signature
 */
export function getInsuranceInformationFormDefaultGroups(): GroupField[] {
  const insuranceDetails: GroupField = {
    id: createId('group'),
    name: 'Insurance Details',
    required: false,
    autoFill: false,
    fields: [
      {
        id: createId('field'),
        type: 'Input Field',
        label: 'Insurance Carrier',
      },
      {
        id: createId('form-row'),
        type: 'Form Row',
        fields: [
          {
            id: createId('field'),
            type: 'Input Field',
            label: 'Policy Number',
          },
          { id: createId('field'), type: 'Input Field', label: 'Group Number' },
        ] as FormField[],
      } as FormRowField,
      {
        id: createId('field'),
        type: 'Input Field',
        label: 'Subscriber Name',
      },
    ],
  }

  const relationship: GroupField = {
    id: createId('group'),
    name: 'Patient Relationship to Subscriber',
    required: false,
    autoFill: false,
    fields: [
      {
        id: createId('field'),
        type: 'Input Field',
        label: 'Relationship (Self, Spouse, Child, Other)',
      },
    ],
  }

  const signature: GroupField = {
    id: createId('group'),
    name: 'Signature',
    required: false,
    autoFill: false,
    fields: [
      {
        id: createId('field'),
        type: 'Signature Field',
        label: 'Patient/Guardian Signature',
      },
    ],
  }

  return [insuranceDetails, relationship, signature]
}

/**
 * Default form content for "General Consent Form".
 * - Consent Information: Date, description (Rich Text), Patient Signature
 */
export function getGeneralConsentFormDefaultGroups(): GroupField[] {
  const consentInfo: GroupField = {
    id: createId('group'),
    name: 'Consent Information',
    required: false,
    autoFill: false,
    fields: [
      { id: createId('field'), type: 'Date Field', label: 'Consent Date' },
      {
        id: createId('field'),
        type: 'Rich Text',
        label: 'Consent Description',
        placeholder: 'Describe the procedure or treatment being consented to.',
      },
      {
        id: createId('field'),
        type: 'Signature Field',
        label: 'Patient Signature',
      },
    ],
  }

  return [consentInfo]
}

/**
 * Default form content for "HIPAA/Privacy Acknowledgement".
 * - Acknowledgement: Date, acknowledgement text, Patient Signature
 */
export function getHipaPrivacyAcknowledgementDefaultGroups(): GroupField[] {
  const acknowledgement: GroupField = {
    id: createId('group'),
    name: 'Privacy Acknowledgement',
    required: false,
    autoFill: false,
    fields: [
      {
        id: createId('field'),
        type: 'Date Field',
        label: 'Acknowledgement Date',
      },
      {
        id: createId('field'),
        type: 'Rich Text',
        label: 'HIPAA Notice',
        placeholder:
          'I acknowledge that I have received and understand the Notice of Privacy Practices.',
      },
      {
        id: createId('field'),
        type: 'Signature Field',
        label: 'Patient Signature',
      },
    ],
  }

  return [acknowledgement]
}

/**
 * Default form content for "Appointment Cancellation Policy".
 * - Policy: Effective date, policy text, acknowledgement checkbox, Signature
 */
export function getAppointmentCancellationPolicyDefaultGroups(): GroupField[] {
  const policy: GroupField = {
    id: createId('group'),
    name: 'Cancellation Policy',
    required: false,
    autoFill: false,
    fields: [
      {
        id: createId('field'),
        type: 'Date Field',
        label: 'Policy Effective Date',
      },
      {
        id: createId('field'),
        type: 'Rich Text',
        label: 'Policy Terms',
        placeholder:
          'e.g. Please provide at least 24 hours notice for cancellations...',
      },
      {
        id: createId('field'),
        type: 'Checkbox',
        label: 'I have read and agree to the appointment cancellation policy',
      },
      {
        id: createId('field'),
        type: 'Signature Field',
        label: 'Patient Signature',
      },
    ],
  }

  return [policy]
}

/** Map (categoryTitle, templateName) -> default groups. Returns null if no mock for that template. */
const TEMPLATE_DEFAULTS: Record<string, Record<string, () => GroupField[]>> = {
  'Patient Information': {
    'New Patient Form': getNewPatientFormDefaultGroups,
    'Returning Patient Form': getReturningPatientFormDefaultGroups,
    'Insurance Information Form': getInsuranceInformationFormDefaultGroups,
  },
  'Consent Forms': {
    'General Consent Form': getGeneralConsentFormDefaultGroups,
    'HIPAA/Privacy Acknowledgement': getHipaPrivacyAcknowledgementDefaultGroups,
    'Appointment Cancellation Policy':
      getAppointmentCancellationPolicyDefaultGroups,
  },
}

export function getDefaultGroupsForTemplate(
  categoryTitle: string | null,
  templateName: string | null,
): GroupField[] | null {
  if (!categoryTitle || !templateName) return null
  const categoryMap = TEMPLATE_DEFAULTS[categoryTitle]
  if (!categoryMap) return null
  const getter = categoryMap[templateName]
  if (!getter) return null
  return getter()
}
